/**
 * Identity Engine API 服务
 * v9.0 新增：AI 数字分身管理功能
 * 
 * 调用路径：前端 -> Agent Admin -> Identity Engine
 * 参考文档：docs/shared/api-protocol/identity-engine-api.md
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type { SuccessResponse } from '@/shared/types/api'
import type {
  Identity,
  IdentityListParams,
  IdentityListResponse,
  CreateIdentityRequest,
  UpdateIdentityRequest,
  Corpus,
  CorpusListParams,
  CorpusListResponse,
  UploadCorpusRequest,
  UploadCorpusResponse,
  ImportCorpusResponse,
  LearningStatus,
  StartLearningRequest,
  IdentityProfile,
  UpdateProfileRequest,
  UpdateBoundaryRequest,
  Voice,
  VoiceSample,
  VoiceSampleSummary,
  VoiceTrainingStatus,
  GetUploadUrlRequest,
  UploadUrlResponse,
  ConfirmVoiceSamplesRequest,
  ConfirmVoiceSamplesResponse,
  StartVoiceTrainingRequest,
  StartVoiceTrainingResponse,
  VoicePreviewRequest,
  VoicePreviewResponse,
  CreateVoiceRequest,
  IdentityVersion,
  VersionCompareResult,
  PublishVersionRequest,
  ConfirmAvatarRequest,
  PaginationInfo,
  // v10.1 新增
  PublishVoiceRequest,
  PublishVoiceResponse,
  UnpublishVoiceResponse,
  UpdateVoiceConfigRequest,
  UpdateVoiceConfigResponse,
  // v10.2 新增
  ListVoiceModelsResponse,
  ActivateVoiceModelResponse,
  DeleteVoiceModelResponse,
  RenameVoiceModelRequest,
  RenameVoiceModelResponse,
  // v1.7 新增：系统配置
  SystemConfig,
  VoiceTrainingModels,
  LearningLLMModelOption,
  // v10.4 新增：情感指令
  VoiceInstructionsConfig,
} from '@/modules/identity/types/identity'
import type { FacetVoiceBinding } from '@/modules/identity/types/facet'

/**
 * Identity Engine API 基础路径
 * 由 Agent Admin 代理转发
 */
const BASE_PATH = '/identity'

/**
 * Identity Engine API 服务
 */
export class IdentityApiService {
  // ==================== 身份管理 ====================

  /**
   * 获取身份列表
   * GET /api/v1/identity-engine/identities
   */
  static async list(
    params?: IdentityListParams
  ): Promise<SuccessResponse<IdentityListResponse>> {
    // 后端返回 { items: Identity[], pagination: {...} }
    const response = await ApiClient.getWithClient<{ items: Identity[]; pagination: PaginationInfo }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities`,
      { params }
    )
    return {
      ...response,
      data: {
        data: response.data.items || [],  // 后端使用 items 字段
        pagination: response.data.pagination,
      },
    }
  }

  /**
   * 创建身份
   * POST /api/v1/identity-engine/identities
   */
  static async create(
    data: CreateIdentityRequest
  ): Promise<SuccessResponse<Identity>> {
    return ApiClient.postWithClient<Identity>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities`,
      data
    )
  }

  /**
   * 获取身份详情
   * GET /api/v1/identity-engine/identities/{identity_id}
   */
  static async get(
    identityId: string
  ): Promise<SuccessResponse<Identity>> {
    return ApiClient.getWithClient<Identity>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}`
    )
  }

  /**
   * 更新身份
   * PATCH /api/v1/identity-engine/identities/{identity_id}
   */
  static async update(
    identityId: string,
    data: UpdateIdentityRequest
  ): Promise<SuccessResponse<Identity>> {
    return ApiClient.patchWithClient<Identity>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}`,
      data
    )
  }

  /**
   * 删除身份
   * DELETE /api/v1/identity-engine/identities/{identity_id}
   */
  static async delete(
    identityId: string
  ): Promise<SuccessResponse<{ deleted: boolean; identity_id: string }>> {
    return ApiClient.deleteWithClient<{ deleted: boolean; identity_id: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}`
    )
  }

  /**
   * 归档身份
   * POST /api/v1/identity-engine/identities/{identity_id}/archive
   */
  static async archive(
    identityId: string
  ): Promise<SuccessResponse<Identity>> {
    return ApiClient.postWithClient<Identity>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/archive`
    )
  }

  /**
   * 恢复身份
   * POST /api/v1/identity-engine/identities/{identity_id}/unarchive
   */
  static async unarchive(
    identityId: string
  ): Promise<SuccessResponse<Identity>> {
    return ApiClient.postWithClient<Identity>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/unarchive`
    )
  }

  // ==================== 语料管理 ====================

  /**
   * 获取语料列表
   * GET /api/v1/identity-engine/identities/{identity_id}/corpus
   */
  static async listCorpus(
    identityId: string,
    params?: CorpusListParams
  ): Promise<SuccessResponse<CorpusListResponse>> {
    return ApiClient.getWithClient<CorpusListResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/corpus`,
      { params }
    )
  }

  /**
   * 上传语料（单条/批量）
   * POST /api/v1/identity-engine/identities/{identity_id}/corpus
   */
  static async uploadCorpus(
    identityId: string,
    data: UploadCorpusRequest
  ): Promise<SuccessResponse<UploadCorpusResponse>> {
    return ApiClient.postWithClient<UploadCorpusResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/corpus`,
      data
    )
  }

  /**
   * 批量导入语料（文件上传）
   * POST /api/v1/identity-engine/identities/{identity_id}/corpus/import
   */
  static async importCorpus(
    identityId: string,
    file: File,
    defaultType?: string
  ): Promise<SuccessResponse<ImportCorpusResponse>> {
    const formData = new FormData()
    formData.append('file', file)
    if (defaultType) {
      formData.append('type', defaultType)
    }
    
    const response = await apiClients.agentAdmin.post<SuccessResponse<ImportCorpusResponse>>(
      `${BASE_PATH}/identities/${identityId}/corpus/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  /**
   * 编辑语料（v9.2.2 新增）
   * PATCH /api/v1/identity/identities/{identity_id}/corpus/{corpus_id}
   */
  static async updateCorpus(
    identityId: string,
    corpusId: string,
    data: {
      name?: string
      target_person?: string
      target_person_aliases?: string[]
    }
  ): Promise<SuccessResponse<Corpus>> {
    return ApiClient.patchWithClient<Corpus>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/corpus/${corpusId}`,
      data
    )
  }

  /**
   * 删除语料
   * DELETE /api/v1/identity-engine/identities/{identity_id}/corpus/{corpus_id}
   */
  static async deleteCorpus(
    identityId: string,
    corpusId: string
  ): Promise<SuccessResponse<{ deleted: boolean }>> {
    return ApiClient.deleteWithClient<{ deleted: boolean }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/corpus/${corpusId}`
    )
  }

  /**
   * 批量删除语料
   * POST /api/v1/identity/identities/{identity_id}/corpus/batch-delete
   */
  static async batchDeleteCorpus(
    identityId: string,
    corpusIds: string[]
  ): Promise<SuccessResponse<{ deleted_count: number }>> {
    return ApiClient.postWithClient<{ deleted_count: number }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/corpus/batch-delete`,
      { corpus_ids: corpusIds }
    )
  }

  // ==================== 人格学习 ====================

  /**
   * 开始学习
   * POST /api/v1/identity-engine/identities/{identity_id}/learn
   */
  static async startLearning(
    identityId: string,
    data?: StartLearningRequest
  ): Promise<SuccessResponse<LearningStatus>> {
    return ApiClient.postWithClient<LearningStatus>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/learn`,
      data || {}
    )
  }

  /**
   * 获取学习状态
   * GET /api/v1/identity-engine/identities/{identity_id}/learn/status
   */
  static async getLearningStatus(
    identityId: string
  ): Promise<SuccessResponse<LearningStatus>> {
    return ApiClient.getWithClient<LearningStatus>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/learn/status`
    )
  }

  /**
   * 取消学习
   * POST /api/v1/identity-engine/identities/{identity_id}/learn/cancel
   */
  static async cancelLearning(
    identityId: string
  ): Promise<SuccessResponse<{ cancelled: boolean; job_id: string }>> {
    return ApiClient.postWithClient<{ cancelled: boolean; job_id: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/learn/cancel`
    )
  }

  /**
   * 获取人格配置
   * GET /api/v1/identity-engine/identities/{identity_id}/profile
   */
  static async getProfile(
    identityId: string
  ): Promise<SuccessResponse<IdentityProfile>> {
    return ApiClient.getWithClient<IdentityProfile>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/profile`
    )
  }

  /**
   * 更新人格配置
   * PATCH /api/v1/identity-engine/identities/{identity_id}/profile
   */
  static async updateProfile(
    identityId: string,
    data: UpdateProfileRequest
  ): Promise<SuccessResponse<IdentityProfile>> {
    return ApiClient.patchWithClient<IdentityProfile>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/profile`,
      data
    )
  }

  /**
   * 更新边界规则
   * PATCH /api/v1/identity-engine/identities/{identity_id}/boundary
   */
  static async updateBoundary(
    identityId: string,
    data: UpdateBoundaryRequest
  ): Promise<SuccessResponse<{ updated: boolean }>> {
    return ApiClient.patchWithClient<{ updated: boolean }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/boundary`,
      data
    )
  }

  /**
   * 导出人格配置
   * POST /api/v1/identity/identities/{identity_id}/profile/export
   */
  static async exportProfile(
    identityId: string,
    options?: { include_inject_text?: boolean; include_voice_model?: boolean }
  ): Promise<SuccessResponse<Record<string, unknown>>> {
    return ApiClient.postWithClient<Record<string, unknown>>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/profile/export`,
      options || {}
    )
  }

  // ==================== 声音克隆（多音色） ====================

  /**
   * 获取音色列表
   * GET /api/v1/identity-engine/identities/{identity_id}/voices
   */
  static async listVoices(
    identityId: string
  ): Promise<SuccessResponse<Voice[]>> {
    return ApiClient.getWithClient<Voice[]>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices`
    )
  }

  /**
   * 创建新音色
   * POST /api/v1/identity-engine/identities/{identity_id}/voices
   */
  static async createVoice(
    identityId: string,
    data: CreateVoiceRequest
  ): Promise<SuccessResponse<Voice>> {
    return ApiClient.postWithClient<Voice>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices`,
      data
    )
  }

  /**
   * 设为默认音色
   * PUT /api/v1/identity-engine/identities/{identity_id}/voices/{voice_id}/default
   */
  static async setDefaultVoice(
    identityId: string,
    voiceId: string
  ): Promise<SuccessResponse<{ message: string }>> {
    return ApiClient.putWithClient<{ message: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/default`
    )
  }

  /**
   * 删除音色
   * DELETE /api/v1/identity-engine/identities/{identity_id}/voices/{voice_id}
   */
  static async deleteVoice(
    identityId: string,
    voiceId: string
  ): Promise<SuccessResponse<{ message: string }>> {
    return ApiClient.deleteWithClient<{ message: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}`
    )
  }

  /**
   * 获取指定音色的音频样本列表
   * GET /api/v1/identity-engine/identities/{identity_id}/voices/{voice_id}/samples
   */
  static async listVoiceSamplesByVoice(
    identityId: string,
    voiceId: string
  ): Promise<SuccessResponse<{ data: VoiceSample[]; summary: VoiceSampleSummary }>> {
    return ApiClient.getWithClient<{ data: VoiceSample[]; summary: VoiceSampleSummary }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/samples`
    )
  }

  // ==================== 声音样本（兼容老接口） ====================

  /**
   * 获取音频样本列表（默认音色）
   * GET /api/v1/identity-engine/identities/{identity_id}/voice/samples
   */
  static async listVoiceSamples(
    identityId: string
  ): Promise<SuccessResponse<{ data: VoiceSample[]; summary: VoiceSampleSummary }>> {
    return ApiClient.getWithClient<{ data: VoiceSample[]; summary: VoiceSampleSummary }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voice/samples`
    )
  }

  /**
   * 获取音频上传凭证（旧版，不带 voiceId）
   * POST /api/v1/identity-engine/identities/{identity_id}/voice/upload-url
   * @deprecated 请使用 getVoiceUploadUrlByVoice
   */
  static async getVoiceUploadUrl(
    identityId: string,
    data: GetUploadUrlRequest
  ): Promise<SuccessResponse<UploadUrlResponse>> {
    return ApiClient.postWithClient<UploadUrlResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voice/upload-url`,
      data
    )
  }

  /**
   * 获取音频上传凭证（多音色版本）
   * POST /api/v1/identity-engine/identities/{identity_id}/voices/{voice_id}/upload-url
   */
  static async getVoiceUploadUrlByVoice(
    identityId: string,
    voiceId: string,
    data: GetUploadUrlRequest
  ): Promise<SuccessResponse<UploadUrlResponse>> {
    return ApiClient.postWithClient<UploadUrlResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/upload-url`,
      data
    )
  }

  /**
   * 确认音频上传（旧版，不带 voiceId）
   * POST /api/v1/identity-engine/identities/{identity_id}/voice/samples
   * @deprecated 请使用 confirmVoiceSamplesByVoice
   */
  static async confirmVoiceSamples(
    identityId: string,
    data: ConfirmVoiceSamplesRequest
  ): Promise<SuccessResponse<ConfirmVoiceSamplesResponse>> {
    return ApiClient.postWithClient<ConfirmVoiceSamplesResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voice/samples`,
      data
    )
  }

  /**
   * 确认音频上传（多音色版本）
   * POST /api/v1/identity-engine/identities/{identity_id}/voices/{voice_id}/samples
   */
  static async confirmVoiceSamplesByVoice(
    identityId: string,
    voiceId: string,
    data: ConfirmVoiceSamplesRequest
  ): Promise<SuccessResponse<ConfirmVoiceSamplesResponse>> {
    return ApiClient.postWithClient<ConfirmVoiceSamplesResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/samples`,
      data
    )
  }

  /**
   * 删除音频样本（旧版，不带 voiceId）
   * DELETE /api/v1/identity-engine/identities/{identity_id}/voice/samples/{sample_id}
   * @deprecated 请使用 deleteVoiceSampleByVoice
   */
  static async deleteVoiceSample(
    identityId: string,
    sampleId: string
  ): Promise<SuccessResponse<{ deleted: boolean }>> {
    return ApiClient.deleteWithClient<{ deleted: boolean }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voice/samples/${sampleId}`
    )
  }

  /**
   * 删除音频样本（多音色版本）
   * DELETE /api/v1/identity-engine/identities/{identity_id}/voices/{voice_id}/samples/{sample_id}
   */
  static async deleteVoiceSampleByVoice(
    identityId: string,
    voiceId: string,
    sampleId: string
  ): Promise<SuccessResponse<{ deleted: boolean }>> {
    return ApiClient.deleteWithClient<{ deleted: boolean }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/samples/${sampleId}`
    )
  }

  /**
   * 开始声音克隆训练
   * POST /api/v1/identity/identities/{identity_id}/voices/{voice_id}/train
   */
  static async startVoiceTraining(
    identityId: string,
    voiceId: string,
    data: StartVoiceTrainingRequest
  ): Promise<SuccessResponse<StartVoiceTrainingResponse>> {
    return ApiClient.postWithClient<StartVoiceTrainingResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/train`,
      data
    )
  }

  /**
   * 🆕 v10.3 获取训练进度 SSE URL
   * GET /api/v1/identity/identities/{identity_id}/voices/{voice_id}/training-progress
   * 
   * 参考：API 文档 6.21 节
   * 
   * SSE 事件：
   * - progress: { phase, current, total, percent, message }
   * - complete: { status, model_id, cosyvoice_status, message }
   * - error: { code, message }
   * 
   * 注意：此方法返回 SSE 的 URL，需要使用 EventSource 连接
   */
  static getTrainingProgressUrl(
    identityId: string,
    voiceId: string,
    provider: string = 'cosyvoice',
    targetModel: string = 'cosyvoice-v3-flash'
  ): string {
    const baseUrl = apiClients.agentAdmin.defaults.baseURL || ''
    // EventSource 不支持自定义 header，需要通过 query parameter 传递 token
    const token = localStorage.getItem('axiom_access_token') || ''
    return `${baseUrl}${BASE_PATH}/identities/${identityId}/voices/${voiceId}/training-progress?provider=${provider}&target_model=${targetModel}&token=${encodeURIComponent(token)}`
  }

  /**
   * 获取声音克隆状态
   * GET /api/v1/identity-engine/identities/{identity_id}/voice/status
   */
  static async getVoiceStatus(
    identityId: string
  ): Promise<SuccessResponse<VoiceTrainingStatus>> {
    return ApiClient.getWithClient<VoiceTrainingStatus>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voice/status`
    )
  }

  /**
   * 取消声音克隆训练
   * POST /api/v1/identity-engine/identities/{identity_id}/voice/cancel
   */
  static async cancelVoiceTraining(
    identityId: string
  ): Promise<SuccessResponse<{ cancelled: boolean; job_id: string }>> {
    return ApiClient.postWithClient<{ cancelled: boolean; job_id: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voice/cancel`
    )
  }

  /**
   * 声音试听（TTS 合成）
   * POST /api/v1/identity/identities/{identity_id}/voices/{voice_id}/preview
   */
  static async previewVoice(
    identityId: string,
    voiceId: string,
    data: VoicePreviewRequest
  ): Promise<SuccessResponse<VoicePreviewResponse>> {
    return ApiClient.postWithClient<VoicePreviewResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/preview`,
      data
    )
  }

  // ==================== v10.1 声音发布管理 ====================

  /**
   * 发布音色
   * PUT /api/v1/identity/{identity_id}/voices/{voice_id}/publish
   * 
   * @param identityId - 身份 ID
   * @param voiceId - 音色 ID
   * @param data - 合成参数配置
   */
  static async publishVoice(
    identityId: string,
    voiceId: string,
    data?: PublishVoiceRequest
  ): Promise<SuccessResponse<PublishVoiceResponse>> {
    return ApiClient.putWithClient<PublishVoiceResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/publish`,
      data || {}
    )
  }

  /**
   * 取消发布音色
   * DELETE /api/v1/identity/{identity_id}/voices/{voice_id}/publish
   * 
   * 注意：如果音色已被 Facet 绑定，会返回 VOICE_IN_USE 错误
   */
  static async unpublishVoice(
    identityId: string,
    voiceId: string
  ): Promise<SuccessResponse<UnpublishVoiceResponse>> {
    return ApiClient.deleteWithClient<UnpublishVoiceResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/publish`
    )
  }

  /**
   * 更新音色合成配置
   * PATCH /api/v1/identity/{identity_id}/voices/{voice_id}/config
   * 
   * 前置条件：音色状态必须是 ready 或 published
   */
  static async updateVoiceConfig(
    identityId: string,
    voiceId: string,
    data: UpdateVoiceConfigRequest
  ): Promise<SuccessResponse<UpdateVoiceConfigResponse>> {
    return ApiClient.patchWithClient<UpdateVoiceConfigResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/config`,
      data
    )
  }

  // ==================== v10.2 训练模型历史管理 ====================

  /**
   * 获取训练模型历史
   * GET /api/v1/identity/{identity_id}/voices/{voice_id}/models
   * 
   * 参考：API 文档 6.15 节
   */
  static async listVoiceModels(
    identityId: string,
    voiceId: string
  ): Promise<SuccessResponse<ListVoiceModelsResponse>> {
    return ApiClient.getWithClient<ListVoiceModelsResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/models`
    )
  }

  /**
   * 激活训练模型
   * PUT /api/v1/identity/{identity_id}/voices/{voice_id}/models/{model_id}/activate
   * 
   * 参考：API 文档 6.16 节
   */
  static async activateVoiceModel(
    identityId: string,
    voiceId: string,
    modelId: string
  ): Promise<SuccessResponse<ActivateVoiceModelResponse>> {
    return ApiClient.putWithClient<ActivateVoiceModelResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/models/${encodeURIComponent(modelId)}/activate`
    )
  }

  /**
   * 删除训练模型（同步删除 CosyVoice 平台）
   * DELETE /api/v1/identity/{identity_id}/voices/{voice_id}/models/{model_id}
   * 
   * 参考：API 文档 6.17 节
   */
  static async deleteVoiceModel(
    identityId: string,
    voiceId: string,
    modelId: string
  ): Promise<SuccessResponse<DeleteVoiceModelResponse>> {
    return ApiClient.deleteWithClient<DeleteVoiceModelResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/models/${encodeURIComponent(modelId)}`
    )
  }

  /**
   * 重命名训练模型
   * PATCH /api/v1/identity/{identity_id}/voices/{voice_id}/models/{model_id}
   * 
   * 参考：API 文档 6.18 节
   */
  static async renameVoiceModel(
    identityId: string,
    voiceId: string,
    modelId: string,
    data: RenameVoiceModelRequest
  ): Promise<SuccessResponse<RenameVoiceModelResponse>> {
    return ApiClient.patchWithClient<RenameVoiceModelResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/${voiceId}/models/${encodeURIComponent(modelId)}`,
      data
    )
  }

  /**
   * 获取 CosyVoice 状态同步 SSE URL
   * GET /api/v1/identity/{identity_id}/voices/cosyvoice-sync (SSE)
   * 
   * 参考：API 文档 6.19 节
   * 
   * 注意：此方法返回 SSE 的 URL，需要使用 EventSource 连接
   */
  static getCosyVoiceSyncUrl(identityId: string): string {
    const baseUrl = apiClients.agentAdmin.defaults.baseURL || ''
    return `${baseUrl}${BASE_PATH}/identities/${identityId}/voices/cosyvoice-sync`
  }

  /**
   * 删除孤儿音色（CosyVoice 上存在但系统无记录）
   * DELETE /api/v1/identity/{identity_id}/voices/orphaned/{model_id}
   * 
   * 参考：API 文档 6.20 节
   */
  static async deleteOrphanedVoice(
    identityId: string,
    modelId: string
  ): Promise<SuccessResponse<{ model_id: string; deleted: boolean }>> {
    return ApiClient.deleteWithClient<{ model_id: string; deleted: boolean }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/voices/orphaned/${encodeURIComponent(modelId)}`
    )
  }

  // ==================== v10.2 场景人格音色绑定 ====================

  /**
   * 绑定音色卡片到场景人格
   * PUT /api/v1/identity/{identity_id}/facets/{scene_id}/voice-binding
   * 
   * 参考：API 文档 8.10 节
   */
  static async bindVoiceToFacet(
    identityId: string,
    sceneId: string,
    voiceCardId: string
  ): Promise<SuccessResponse<{ scene_id: string; voice_binding: FacetVoiceBinding; bound_at: string }>> {
    return ApiClient.putWithClient<{ scene_id: string; voice_binding: FacetVoiceBinding; bound_at: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/voice-binding`,
      { voice_card_id: voiceCardId }
    )
  }

  /**
   * 获取场景人格的音色绑定信息
   * GET /api/v1/identity/{identity_id}/facets/{scene_id}/voice-binding
   * 
   * 参考：API 文档 8.11 节
   * 
   * 响应格式: { scene_id, voice_binding, bound_at }
   */
  static async getFacetVoiceBinding(
    identityId: string,
    sceneId: string
  ): Promise<SuccessResponse<{ scene_id: string; voice_binding: FacetVoiceBinding | null; bound_at: string | null }>> {
    return ApiClient.getWithClient<{ scene_id: string; voice_binding: FacetVoiceBinding | null; bound_at: string | null }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/voice-binding`
    )
  }

  /**
   * 解除场景人格音色绑定
   * DELETE /api/v1/identity/{identity_id}/facets/{scene_id}/voice-binding
   * 
   * 参考：API 文档 8.12 节
   */
  static async unbindVoiceFromFacet(
    identityId: string,
    sceneId: string
  ): Promise<SuccessResponse<{ scene_id: string; unbound: boolean; message: string }>> {
    return ApiClient.deleteWithClient<{ scene_id: string; unbound: boolean; message: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/voice-binding`
    )
  }

  /**
   * 🆕 v1.6: 调整场景人格音色配置
   * PATCH /api/v1/identity/{identity_id}/facets/{scene_id}/voice-binding
   * 
   * 无需解绑重新绑定，直接更新配置
   * v1.7 更新：支持 model_id 切换训练模型
   * v10.4 更新：支持 instruction 情感指令
   * 
   * 参考：API 文档 8.13 节
   */
  static async updateVoiceBindingConfig(
    identityId: string,
    sceneId: string,
    config: {
      model_id?: string      // v1.7: 切换训练模型（必须属于当前绑定的音色卡片）
      speech_rate?: number
      pitch_rate?: number
      volume?: number
      instruction?: string   // v10.4: 情感指令（空字符串表示清除）
    }
  ): Promise<SuccessResponse<{
    scene_id: string
    voice_binding: FacetVoiceBinding
    updated_fields: string[]
    message: string
  }>> {
    return ApiClient.patchWithClient<{
      scene_id: string
      voice_binding: FacetVoiceBinding
      updated_fields: string[]
      message: string
    }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/voice-binding`,
      config
    )
  }

  // ==================== 版本管理 ====================

  /**
   * 获取版本历史
   * GET /api/v1/identity-engine/identities/{identity_id}/versions
   */
  static async listVersions(
    identityId: string
  ): Promise<SuccessResponse<IdentityVersion[]>> {
    return ApiClient.getWithClient<IdentityVersion[]>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/versions`
    )
  }

  /**
   * 发布新版本
   * POST /api/v1/identity-engine/identities/{identity_id}/versions
   */
  static async publishVersion(
    identityId: string,
    data: PublishVersionRequest
  ): Promise<SuccessResponse<IdentityVersion>> {
    return ApiClient.postWithClient<IdentityVersion>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/versions`,
      data
    )
  }

  /**
   * 获取指定版本详情
   * GET /api/v1/identity-engine/identities/{identity_id}/versions/{version}
   */
  static async getVersion(
    identityId: string,
    version: number
  ): Promise<SuccessResponse<IdentityVersion>> {
    return ApiClient.getWithClient<IdentityVersion>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/versions/${version}`
    )
  }

  /**
   * 版本对比
   * GET /api/v1/identity-engine/identities/{identity_id}/versions/compare
   */
  static async compareVersions(
    identityId: string,
    fromVersion: number,
    toVersion: number
  ): Promise<SuccessResponse<VersionCompareResult>> {
    return ApiClient.getWithClient<VersionCompareResult>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/versions/compare`,
      { params: { from_version: fromVersion, to_version: toVersion } }
    )
  }

  /**
   * 回滚版本
   * POST /api/v1/identity-engine/identities/{identity_id}/versions/{version}/rollback
   */
  static async rollbackVersion(
    identityId: string,
    version: number
  ): Promise<SuccessResponse<{ new_version: number; message: string }>> {
    return ApiClient.postWithClient<{ new_version: number; message: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/versions/${version}/rollback`
    )
  }

  // ==================== 头像上传 ====================

  /**
   * 获取头像上传凭证
   * POST /api/v1/identity-engine/identities/{identity_id}/avatar/upload-url
   */
  static async getAvatarUploadUrl(
    identityId: string,
    data: GetUploadUrlRequest
  ): Promise<SuccessResponse<UploadUrlResponse>> {
    return ApiClient.postWithClient<UploadUrlResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/avatar/upload-url`,
      data
    )
  }

  /**
   * 确认头像上传
   * POST /api/v1/identity-engine/identities/{identity_id}/avatar
   */
  static async confirmAvatar(
    identityId: string,
    data: ConfirmAvatarRequest
  ): Promise<SuccessResponse<{ avatar_url: string }>> {
    return ApiClient.postWithClient<{ avatar_url: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/avatar`,
      data
    )
  }
}

// ==================== 辅助函数 ====================

/**
 * 上传文件到 S3（通过预签名 URL）
 */
export async function uploadToS3(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  console.log('[S3 Upload] Starting upload to:', uploadUrl.substring(0, 100) + '...')
  console.log('[S3 Upload] File:', file.name, 'Size:', file.size, 'Type:', contentType)
  
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  })
  
  if (!response.ok) {
    const errorText = await response.text()
    console.error('[S3 Upload] Failed:', response.status, errorText)
    throw new Error(`S3 上传失败: ${response.status} - ${errorText}`)
  }
  
  console.log('[S3 Upload] Success!')
}

/**
 * 上传头像（完整流程）
 */
export async function uploadAvatar(
  identityId: string,
  file: File
): Promise<string> {
  // 1. 获取上传凭证
  const urlResponse = await IdentityApiService.getAvatarUploadUrl(identityId, {
    file_name: file.name,
    content_type: file.type,
  })
  
  // 2. 上传到 S3
  await uploadToS3(urlResponse.data.upload_url, file, file.type)
  
  // 3. 确认上传
  const confirmResponse = await IdentityApiService.confirmAvatar(identityId, {
    file_key: urlResponse.data.file_key,
  })
  
  return confirmResponse.data.avatar_url
}

/**
 * 上传音频样本（完整流程）- 旧版，不带 voiceId
 * @deprecated 请使用 uploadVoiceSampleByVoice
 */
export async function uploadVoiceSample(
  identityId: string,
  file: File,
  duration: number
): Promise<ConfirmVoiceSamplesResponse> {
  // 1. 获取上传凭证
  const urlResponse = await IdentityApiService.getVoiceUploadUrl(identityId, {
    file_name: file.name,
    content_type: file.type || 'audio/wav',
  })
  
  // 2. 上传到 S3
  await uploadToS3(urlResponse.data.upload_url, file, file.type || 'audio/wav')
  
  // 3. 确认上传
  const confirmResponse = await IdentityApiService.confirmVoiceSamples(identityId, {
    samples: [{
      file_key: urlResponse.data.file_key,
      file_name: file.name,
      duration,
    }],
  })
  
  return confirmResponse.data
}

/**
 * 上传音频样本（完整流程）- 多音色版本
 */
export async function uploadVoiceSampleByVoice(
  identityId: string,
  voiceId: string,
  file: File,
  duration: number
): Promise<ConfirmVoiceSamplesResponse> {
  // 1. 获取上传凭证
  const urlResponse = await IdentityApiService.getVoiceUploadUrlByVoice(identityId, voiceId, {
    file_name: file.name,
    content_type: file.type || 'audio/wav',
  })
  
  // 2. 上传到 S3
  await uploadToS3(urlResponse.data.upload_url, file, file.type || 'audio/wav')
  
  // 3. 确认上传
  const confirmResponse = await IdentityApiService.confirmVoiceSamplesByVoice(identityId, voiceId, {
    samples: [{
      file_key: urlResponse.data.file_key,
      file_name: file.name,
      duration,
    }],
  })
  
  return confirmResponse.data
}


// ==================== v10.3 场景配置 API ====================

/**
 * 场景配置
 */
export interface SceneConfig {
  scene_tag: string    // 场景标签（唯一标识）
  name: string         // 中文名称
  name_en: string      // 英文名称
  icon: string         // 图标
  description: string  // 描述
  order: number        // 排序权重
}

/**
 * 获取所有场景列表
 * GET /api/v1/identity/scenes
 */
export async function listScenes(): Promise<SceneConfig[]> {
  const response = await ApiClient.getWithClient<SceneConfig[]>(
    apiClients.agentAdmin,
    `${BASE_PATH}/scenes`
  )
  return response.data
}

/**
 * 获取指定场景信息
 * GET /api/v1/identity/scenes/{scene_tag}
 */
export async function getScene(sceneTag: string): Promise<SceneConfig> {
  const response = await ApiClient.getWithClient<SceneConfig>(
    apiClients.agentAdmin,
    `${BASE_PATH}/scenes/${sceneTag}`
  )
  return response.data
}


// ==================== v1.7 系统配置 API ====================

/**
 * 获取所有系统配置
 * GET /api/v1/identity/config
 * 
 * 一次性获取所有可用选项，减少 API 调用次数
 */
export async function getConfig(): Promise<SystemConfig> {
  const response = await ApiClient.getWithClient<SystemConfig>(
    apiClients.agentAdmin,
    `${BASE_PATH}/config`
  )
  return response.data
}

/**
 * 获取声音训练可用模型
 * GET /api/v1/identity/config/voice-training-models
 */
export async function getVoiceTrainingModels(): Promise<VoiceTrainingModels> {
  const response = await ApiClient.getWithClient<VoiceTrainingModels>(
    apiClients.agentAdmin,
    `${BASE_PATH}/config/voice-training-models`
  )
  return response.data
}

/**
 * 获取语料学习可用 LLM 模型
 * GET /api/v1/identity/config/learning-llm-models
 */
export async function getLearningLLMModels(): Promise<LearningLLMModelOption[]> {
  const response = await ApiClient.getWithClient<LearningLLMModelOption[]>(
    apiClients.agentAdmin,
    `${BASE_PATH}/config/learning-llm-models`
  )
  return response.data
}

/**
 * 获取情感指令配置
 * GET /api/v1/identity/config/voice-instructions
 * 
 * 返回可用的情绪类和方言类指令，以及各模型的支持情况
 * @see v10.4 情感指令功能
 */
export async function getVoiceInstructions(): Promise<VoiceInstructionsConfig> {
  const response = await ApiClient.getWithClient<VoiceInstructionsConfig>(
    apiClients.agentAdmin,
    `${BASE_PATH}/config/voice-instructions`
  )
  return response.data
}
